import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Activity, AlertTriangle, Users, ShieldCheck, MapPin, ArrowRight, UserCheck } from "lucide-react";

export default function AdminSafety() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/safety/overview");
        setData(data);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading || !data) return <div className="hs-overline">LOADING…</div>;
  const { inspections, violations, driver_alerts, totals } = data;

  return (
    <div className="space-y-8" data-testid="admin-safety-page">
      <div>
        <div className="hs-overline text-blue-700">FLEET-WIDE SAFETY</div>
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter mt-2">Safety Operations</h1>
        <p className="text-sm text-slate-500 mt-2 max-w-2xl">
          Inspections, citations and driver credential alerts across every client.
          To log a new inspection or violation, open any client → Safety tab.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat icon={Activity} label="Inspections Total" value={totals.inspections_total} />
        <Stat icon={AlertTriangle} label="Violations Total" value={totals.violations_total} />
        <Stat icon={AlertTriangle} label="Open Violations" value={totals.open_violations} accent="#D97706" />
        <Stat icon={Users} label="Active Drivers" value={totals.active_drivers} />
        <Stat icon={UserCheck} label="Expiring 60d" value={totals.expiring_60d} accent={totals.expiring_60d ? "#DC2626" : "#059669"} />
      </div>

      {/* Driver alerts */}
      <div className="hs-card">
        <div className="p-5 border-b border-slate-200">
          <div className="hs-overline">DRIVER CREDENTIAL ALERTS</div>
          <h3 className="font-display font-bold text-xl mt-1">CDL · Medical · Drug Tests expiring soon</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-left">
              <tr>
                <Th>Driver</Th><Th>Client</Th><Th>Kind</Th><Th>Due</Th><Th>Days</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {driver_alerts.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-sm text-slate-500">No expirations in next 60 days.</td></tr>
              )}
              {driver_alerts.map((a, i) => (
                <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3">{a.driver_name}</td>
                  <td className="px-4 py-3 text-slate-500">{a.client_name}</td>
                  <td className="px-4 py-3 font-mono text-xs">{a.kind}</td>
                  <td className="px-4 py-3 font-mono text-xs">{a.due}</td>
                  <td className={`px-4 py-3 font-mono ${a.days_left < 0 ? "text-red-600" : a.severity === "critical" ? "text-red-600" : "text-amber-600"}`}>
                    {a.days_left < 0 ? `${Math.abs(a.days_left)}d over` : `${a.days_left}d`}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/admin/clients/${a.client_id}`} className="hs-btn-ghost text-xs inline-flex items-center gap-1" data-testid={`alert-client-link-${i}`}>
                      OPEN <ArrowRight size={12}/>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="hs-card">
          <div className="p-5 border-b border-slate-200">
            <div className="hs-overline">RECENT · ALL CLIENTS</div>
            <h3 className="font-display font-bold text-xl mt-1">DOT Inspections</h3>
          </div>
          <div className="divide-y divide-slate-100">
            {inspections.length === 0 && <div className="p-6 text-sm text-slate-500">No inspections logged.</div>}
            {inspections.slice(0, 10).map((i) => (
              <Link key={i.id} to={`/admin/clients/${i.client_id}`} className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-slate-50">
                <div className="min-w-0 flex-1">
                  <div className="text-sm inline-flex items-center gap-1"><MapPin size={12} className="text-slate-400"/>{i.location}</div>
                  <div className="text-xs text-slate-500">{i.client_name} · Level {i.level} · {i.violations_count} violation(s)</div>
                </div>
                <div className="text-xs font-mono text-slate-500">{(i.date || "").slice(0, 10)}</div>
                {i.oos && <span className="hs-tag hs-tag-danger">OOS</span>}
              </Link>
            ))}
          </div>
        </div>
        <div className="hs-card">
          <div className="p-5 border-b border-slate-200">
            <div className="hs-overline">RECENT · ALL CLIENTS</div>
            <h3 className="font-display font-bold text-xl mt-1">Violations</h3>
          </div>
          <div className="divide-y divide-slate-100">
            {violations.length === 0 && <div className="p-6 text-sm text-slate-500">No violations logged.</div>}
            {violations.slice(0, 10).map((v) => (
              <Link key={v.id} to={`/admin/clients/${v.client_id}`} className="flex items-center justify-between gap-3 px-5 py-3 hover:bg-slate-50">
                <div className="min-w-0 flex-1">
                  <div className="text-sm">{v.code} — <span className="text-slate-500">{v.description}</span></div>
                  <div className="text-xs text-slate-500">{v.client_name}</div>
                </div>
                <span className={`hs-tag ${v.status === "open" ? "hs-tag-warn" : v.status === "paid" ? "hs-tag-success" : "hs-tag-info"}`}>{(v.status || "").toUpperCase()}</span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Th({ children }) { return <th className="px-4 py-3 hs-overline">{children}</th>; }

function Stat({ icon: Icon, label, value, accent = "#1D4ED8" }) {
  return (
    <div className="hs-card p-4">
      <div className="flex items-center justify-between">
        <span className="hs-overline">{label}</span>
        <Icon size={14} style={{ color: accent }}/>
      </div>
      <div className="font-mono font-black text-3xl mt-2" style={{ color: accent }}>{value}</div>
    </div>
  );
}
